import {Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {formatCurrency} from "@angular/common";
import {MatTableDataSource} from "@angular/material/table";
import {DataFormatService} from "../../../services/data-format.service";
import {MatSort} from "@angular/material/sort";
import {update_has_set_asides, update_project_set_asides, update_all_project_set_aside_data,
  delete_unit_set_aside, add_set_aside_type} from "./graphql/setAsides";
import {Apollo} from "apollo-angular";
import {MatSnackBar, MatSnackBarConfig} from "@angular/material/snack-bar";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmationModalComponent} from "../../../components/confirmation-modal/confirmation-modal.component";
import {AddSetAsideTypeConfirmationComponent} from "./add-set-aside-type-confirmation/add-set-aside-type-confirmation.component";

const UPDATE_HAS_SET_ASIDES = update_has_set_asides;
const UPDATE_PROJECT_SET_ASIDES = update_project_set_asides;
const UPDATE_ALL_PROJECT_SET_ASIDE_DATA = update_all_project_set_aside_data;
const DELETE_UNIT_SET_ASIDE = delete_unit_set_aside;
const ADD_SET_ASIDE_TYPE =add_set_aside_type;
export interface EconomicImpactTable {
  field: string;
  formula: string;
  value: string;
}

export interface SetAsideTable {
  setasideid: string;
  type: string;
  typeid: string;
  typeidnew: string;
  unitsNumber: number;
  unitsNumberNew: number;
  hardBestEffort: string;
  hardBestEffortNew: boolean | string;
}

@Component({
    selector: 'deal-esg-supportive-services',
    templateUrl: './esg-supportive-services.component.html',
    styleUrls: ['./esg-supportive-services.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EsgSupportiveServicesComponent implements OnInit{
  @Input() project: any;
  @Input() permissions: any;
  @Input() setAsideTypes: any;
  @ViewChild('dataSort') public dataSort: MatSort;

  economicColumns: string[] = ['field', 'formula', 'value'];
  economicDataSource: MatTableDataSource<EconomicImpactTable>;
  hasSetAsides: boolean;
  hasSetAsidesString: string;
  setAsideColumns: string[] = ['type', 'unitsNumber', 'hardBestEffort'];
  setAsideDataSource: MatTableDataSource<SetAsideTable>;
  isEditMode: boolean = false;
  setAsides: any;
  enableButtons: boolean = true;

  constructor(private dataFormat: DataFormatService,
              private apollo: Apollo,
              private _snackBar: MatSnackBar,
              private dialog: MatDialog) {}

  ngOnInit() {
    this.economicDataSource = new MatTableDataSource(this.formatLIHTCUnits(this.project.properties));
    this.hasSetAsides = this.project.projectDetail.has_set_asides;
    this.hasSetAsidesString = this.dataFormat.yesNoFormat(this.project.projectDetail.has_set_asides);
    this.setAsides = this.formatSetAsides(this.project.projectSetAsides);
    this.setAsideDataSource = new MatTableDataSource(this.setAsides);
  }

  ngAfterViewInit(){
    this.setAsideDataSource.sort = this.dataSort;
  }

  updateUnits(event: any, index: number){
    this.setAsides[index].unitsNumberNew = event.target.valueAsNumber;
    this.buttonsEnabled();
  }

  formatLIHTCUnits(units: any): Array<EconomicImpactTable>{
    let lihtc: number = 0;
    if(units && units.length > 0){
      for(let i = 0; i < units.length; i++){
        if(units[i].total_lihtc_units){
          lihtc += units[i].total_lihtc_units;
        }
      }
    }
    return [
      {field: 'LIHTC Units', formula: '', value: lihtc.toString()},
      {field: 'Jobs Created', formula: '#LIHTC Units * 1.25', value: (lihtc * 1.25).toString()},
      {field: 'Local Income Generated - First Year', formula: '$7.9M * (#LIHTC Units/100)',
        value: formatCurrency((7900000 * (lihtc / 100)), 'EN', '$', 'USD')},
      {field: 'Local Income Generated - Annual', formula: '$2.4M * (#LIHTC Units/100)',
        value: formatCurrency((2400000 * (lihtc / 100)), 'EN', '$', 'USD')},
      {field: 'Taxes Saved - First Year', formula: '$827k * (#LIHTC Units/100)',
        value: formatCurrency((827000 * (lihtc / 100)), 'EN', '$', 'USD')},
      {field: 'Taxes Saved - Annual', formula: '$441k * (#LIHTC Units/100)',
        value: formatCurrency((441000 * (lihtc / 100)), 'EN', '$', 'USD')}
    ];
  }

  formatSetAsides(units: any){
    let formatted: Array<SetAsideTable> = [];
    if(units){
      for(let i = 0; i < units.length; i++){
        let hardBestEffortString = this.formatHardBestEffortToString(units[i].is_hard);

        formatted.push({
          setasideid: units[i].id,
          type: units[i].setAsideType.name,
          typeid: units[i].setAsideType.id,
          typeidnew: units[i].setAsideType.id,
          unitsNumber: units[i].total,
          unitsNumberNew: units[i].total,
          hardBestEffort: hardBestEffortString,
          hardBestEffortNew: units[i].is_hard !== null ? units[i].is_hard : 'Unknown'
        });
      }
    }
    return formatted;
  }

  formatHardBestEffortToString(hardBestEffort: boolean): string{
    let hardBestEffortString: string;
    if(hardBestEffort === null){
      hardBestEffortString = 'Unknown';
    } else if(hardBestEffort){
      hardBestEffortString = "Hard";
    } else {
      hardBestEffortString = "Best Effort";
    }
    return hardBestEffortString;
  }

  saveSetAsides(){
    if(this.enableButtons){
      if(this.isEditMode){
        let changes = [];
        if(this.setAsides.length > 0){
          for(let i = 0; i < this.setAsides.length; i++){
            let newHardBestEffortString: string;
            if(this.setAsides[i].hardBestEffortNew !== 'Unknown'){
              newHardBestEffortString = this.formatHardBestEffortToString(this.setAsides[i].hardBestEffortNew);
            } else {
              newHardBestEffortString = 'Unknown';
            }

            if(this.setAsides[i].typeid !== this.setAsides[i].typeidnew
              || this.setAsides[i].unitsNumber !== this.setAsides[i].unitsNumberNew
              || this.setAsides[i].hardBestEffort !== newHardBestEffortString){
              if(this.setAsides[i].setasideid === ''){
                changes.push({
                  total: this.setAsides[i].unitsNumberNew,
                  is_hard: newHardBestEffortString !== 'Unknown' ? this.setAsides[i].hardBestEffortNew : null,
                  set_aside_type_id: this.setAsides[i].typeidnew,
                  active: true
                });
              } else {
                changes.push({
                  id: this.setAsides[i].setasideid,
                  total: this.setAsides[i].unitsNumberNew,
                  is_hard: newHardBestEffortString !== 'Unknown' ? this.setAsides[i].hardBestEffortNew : null,
                  set_aside_type_id: this.setAsides[i].typeidnew
                });
              }
            }
          }
        }

        let newBoolString = this.dataFormat.yesNoFormat(this.hasSetAsides);
        if(changes.length > 0 && this.hasSetAsidesString !== newBoolString){
          this.apollo
            .mutate({
              mutation: UPDATE_ALL_PROJECT_SET_ASIDE_DATA,
              variables: {
                id: this.project.projectDetail.id,
                has_set_asides: this.hasSetAsides,
                input: {
                  id: this.project.id,
                  projectSetAsides: {
                    upsert: changes
                  }
                }
              },
            })
            .subscribe(
              ({ data }) => {
                // @ts-ignore
                let results = data.updateProjectSetAsides.projectSetAsides;
                // @ts-ignore
                this.hasSetAsidesString = this.dataFormat.yesNoFormat(data.updateHasSetAsides.has_set_asides);
                this.setAsides = this.formatSetAsides(results);
                this.setAsideDataSource = new MatTableDataSource(this.setAsides);
                this.openSnackBar('Updated Unit Set-Asides');
              }
            );
        }else if(changes.length > 0){
          this.apollo
            .mutate({
              mutation: UPDATE_PROJECT_SET_ASIDES,
              variables: {
                input: {
                  id: this.project.id,
                  projectSetAsides: {
                    upsert: changes
                  }
                }
              },
            })
            .subscribe(
              ({ data }) => {
                // @ts-ignore
                let results = data.updateProjectSetAsides.projectSetAsides;
                this.setAsides = this.formatSetAsides(results);
                this.setAsideDataSource = new MatTableDataSource(this.setAsides);
                this.openSnackBar('Updated Unit Set-Asides');
              }
            );
        } else if(this.hasSetAsidesString !== newBoolString){
          this.apollo
            .mutate({
              mutation: UPDATE_HAS_SET_ASIDES,
              variables: {
                id: this.project.projectDetail.id,
                has_set_asides: this.hasSetAsides
              },
            })
            .subscribe(
              ({ data }) => {
                // @ts-ignore
                this.hasSetAsidesString = this.dataFormat.yesNoFormat(data.updateHasSetAsides.has_set_asides);
                this.openSnackBar('Updated Unit Set-Asides');
              }
            );
        }
      }
      this.isEditMode = !this.isEditMode;
    }
  }

  deleteSetAside(row: any, index: number){
    if(row.setasideid !== ''){
      const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
        ConfirmationModalComponent,
        {
          width: '600px',
          height: '300px',
          data: {
            modalTitle: 'Delete Unit Set-Aside',
            actionMessage: 'Are you sure you want to delete the Unit Set-Aside?',
            modalInformation: [
              {
                title: 'Set-Aside Type',
                info: row.type
              },
              {
                title: 'Number of Units',
                info: row.unitsNumber
              },
              {
                title: 'Hard/Best Effort',
                info: row.hardBestEffort
              }
            ]
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        });
      ref.afterClosed().subscribe((data) => {
        if(data.clicked === 'Ok'){
          this.apollo
            .mutate({
              mutation: DELETE_UNIT_SET_ASIDE,
              variables: {
                id: row.setasideid
              },
            })
            .subscribe(
              ({ data }) => {
                this.setAsides.splice(index, 1);
                this.setAsideDataSource = new MatTableDataSource(this.setAsides);
                this.openSnackBar('Deleted Unit Set-Aside.');
              }
            );
        }
      });
    } else {
      this.setAsides.splice(index, 1);
      this.setAsideDataSource = new MatTableDataSource(this.setAsides);
    }
    this.buttonsEnabled();
  }

  addUnitSetAside(){
    if(this.enableButtons){
      if(this.setAsides.length <= 0){
        let newArray: Array<SetAsideTable> = [];
        this.setAsides = newArray;
      }
      this.setAsides.push({
        setasideid: '',
        type: '',
        typeid: '',
        typeidnew: '',
        unitsNumber: null,
        unitsNumberNew: null,
        hardBestEffort: '',
        hardBestEffortNew: ''
      });
      this.setAsideDataSource = new MatTableDataSource(this.setAsides);
      this.enableButtons = false;
    }
  }

  addUnitSetAsideType(){
    if(this.enableButtons){
      const ref: MatDialogRef<AddSetAsideTypeConfirmationComponent> = this.dialog.open(
        AddSetAsideTypeConfirmationComponent,
        {
          width: '600px',
          data: {
            setAsideTypes: this.setAsideTypes,
            newSetAsideType: ''
          },
          backdropClass: 'confirmDialogComponent',
          hasBackdrop: true,
        });
      ref.afterClosed().subscribe(result => {
        if(result !== undefined){
          let newTypeString = result.newSetAsideType.trim();
          if(newTypeString !== ''){
            this.apollo
              .mutate({
                mutation: ADD_SET_ASIDE_TYPE,
                variables: {
                  name: newTypeString,
                  active: true
                },
              })
              .subscribe(
                ({ data }) => {
                  // @ts-ignore
                  let results = data.addSetAsideType;
                  let typeArray = Object.assign([], this.setAsideTypes);
                  typeArray.push(results);
                  typeArray.sort((a: any, b: any) => a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1);
                  this.setAsideTypes = typeArray;
                  this.openSnackBar('Created Set-Aside Type.');
                }
              );
          }
        }
      });
    }
  }

  buttonsEnabled(){
    if(this.setAsides.length > 0){
      let hasFilledData = true;
      for(let i = 0; i < this.setAsides.length; i++){
        if(this.setAsides[i].typeidnew === '' || this.setAsides[i].typeidnew === null || this.setAsides[i].hardBestEffortNew === ''){
          hasFilledData = false;
          break;
        }
      }
      this.enableButtons = hasFilledData;
    } else {
      this.enableButtons = true;
    }
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = "center";
    config.verticalPosition = "top";
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }
}
